'use client';

import type { QueryClient } from '@tanstack/react-query';
import { buildQueryString, fetchCachedQuery, fetchJson, loadQuery, type QueryLoadOptions } from '@/lib/api/client';
import { callsSearchRequest } from '@/lib/api/keys';
import { SOFTPHONE_CREDENTIALS_TIMEOUT_MS, withTimeout } from '@/lib/softphone-start';

export type DidOption = {
  didNumber: string;
  didId: string;
  poolTier: 'general' | 'priority' | 'personal';
  label: string;
  gatewayName: string | null;
};

/** Which caller ID the picker starts on; mirrors the backend's selection order. */
export type DidChoiceKind = 'personal' | 'pinned' | 'pool';

export type DidCallerIdChoices = {
  personal: DidOption | null;
  pinned: DidOption | null;
  pool: DidOption[];
  recommended: DidChoiceKind;
};
import type {
  CallDetailResponse,
  CallSearchQuery,
  CallSearchResponse,
  CallSummary,
  SessionOriginatePayload,
  SessionOriginateResponse,
  SipStatusResponse,
  TelephonyCallMetricSampleInput,
  TelephonySessionEventInput,
  SoftphoneCredentialsResponse,
} from '@/types/telephony';

const sipQueryKeys = {
  status: ['sip', 'status'] as const,
  callDetail: (callId: string) => ['sip', 'calls-detail', callId] as const,
  didOptions: (country: string, contactId: string, exclude: string) =>
    ['sip', 'did-options', country, contactId, exclude] as const,
};

// Mirrors the backend's parseDidGeography()/callerIdCountries(): the rotation
// candidates depend on the agent's role, the prospect phone's COUNTRY and the
// explicit exclusions. The contact is part of the key too, because the reply
// also carries that contact's pinned number. Keep the prefixes in sync with
// backend/src/services/did-pool-service.ts.
function didOptionsCountry(prospectPhone: string): string {
  if (prospectPhone.startsWith('+1')) return 'US';
  if (prospectPhone.startsWith('+61')) return 'AU';
  if (prospectPhone.startsWith('+47')) return 'NO';
  if (prospectPhone.startsWith('+63')) return 'PH';
  return 'XX';
}

export const sipApi = {
  getStatus(queryClient: QueryClient): Promise<SipStatusResponse> {
    return fetchCachedQuery<SipStatusResponse>(queryClient, sipQueryKeys.status, '/api/sip/status', 2_000);
  },

  searchCalls(
    queryClient: QueryClient,
    query: CallSearchQuery
  ): Promise<CallSearchResponse> {
    const request = callsSearchRequest(query);
    return fetchCachedQuery<{ ok: true; calls: CallSummary[]; total: number; limit: number; offset: number }>(
      queryClient,
      request.key,
      request.path,
      request.staleTime
    ).then((payload) => ({
      calls: payload.calls,
      total: Number(payload.total || 0),
      limit: Number(payload.limit || query.limit || 50),
      offset: Number(payload.offset || query.offset || 0),
    }));
  },

  getCallDetail(queryClient: QueryClient, callId: string): Promise<CallDetailResponse> {
    return fetchCachedQuery<CallDetailResponse>(
      queryClient,
      sipQueryKeys.callDetail(callId),
      `/api/sip/calls/${callId}`,
      0
    );
  },

  hangupCall(callId: string): Promise<void> {
    return withTimeout(
      fetchJson(`/api/sip/calls/${callId}/hangup`, { method: 'POST' }).then(() => undefined),
      8_000,
      'Hangup request timed out. Check your connection and try again.'
    );
  },

  // Bounded here rather than at one call site so all three callers are covered
  // — the softphone start, its background revalidation, and the campaign
  // caller-ID sync, which otherwise hangs with no DID and no error. Aborts
  // rather than races: abandoning the promise leaves the socket, and the
  // upstream work behind it, running, so a stalled fetch plus a retry schedule
  // becomes several concurrent stalled fetches.
  getPhoneCredentials(options: { campaignId?: string } = {}): Promise<SoftphoneCredentialsResponse> {
    const query = buildQueryString({
      campaignId: options.campaignId?.trim(),
    });
    return fetchJson<SoftphoneCredentialsResponse>(`/api/sip/phone/credentials${query}`, {
      signal: AbortSignal.timeout(SOFTPHONE_CREDENTIALS_TIMEOUT_MS),
    }).catch((error: unknown) => {
      // AbortSignal.timeout rejects with a TimeoutError DOMException whose
      // message ("signal timed out") would surface verbatim in the phone widget.
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new Error('The phone service did not respond in time.');
      }
      throw error;
    });
  },

  getRecordingUrl(callLogId: string): string {
    return `/api/sip/recordings/${callLogId}`;
  },

  getRecordingDownloadUrl(callLogId: string): string {
    return `/api/sip/recordings/${callLogId}/download`;
  },

  getTranscriptDownloadUrl(callLogId: string): string {
    return `/api/sip/calls/${callLogId}/transcript`;
  },

  async getDidPreview(params: { phone: string; contactId?: string | null; campaignId?: string | null }): Promise<{ didNumber: string | null; source: string | null }> {
    const qs = buildQueryString({
      phone: params.phone,
      contactId: params.contactId,
      campaignId: params.campaignId,
    });
    return fetchJson<{ ok: true; didNumber: string | null; source: string | null }>(`/api/sip/calls/did-preview${qs}`).then((r) => ({ didNumber: r.didNumber, source: r.source }));
  },

  // 5-minute cache keyed by country + contact + exclusions: the opening
  // rotation candidate is a display-only preview (an un-overridden dial lets
  // the server pick fresh), so slight staleness there is fine and per-dial
  // refetches collapse to ~one per 5 minutes.
  //
  // `fresh` is for "New number": a roll is an action, not a preview. Serving
  // it from cache hands back the number the caller is already looking at
  // whenever the same exclusion list is asked twice — which is every roll
  // after the roll state resets (card 418).
  async getDidOptions(
    queryClient: QueryClient,
    params: { phone: string; contactId?: string | null; campaignId?: string | null; exclude?: string[] },
    options?: QueryLoadOptions
  ): Promise<DidCallerIdChoices> {
    const exclude = params.exclude?.length ? params.exclude.join(',') : undefined;
    const qs = buildQueryString({
      phone: params.phone,
      contactId: params.contactId,
      campaignId: params.campaignId,
      exclude,
    });
    return loadQuery<{ ok: true } & DidCallerIdChoices>(
      queryClient,
      sipQueryKeys.didOptions(didOptionsCountry(params.phone), params.contactId || '', exclude || ''),
      `/api/sip/calls/did-options${qs}`,
      5 * 60_000,
      options
    ).then((r) => ({ personal: r.personal, pinned: r.pinned, pool: r.pool, recommended: r.recommended }));
  },

  originateCall(payload: SessionOriginatePayload): Promise<SessionOriginateResponse> {
    return withTimeout(
      fetchJson<SessionOriginateResponse>('/api/sip/calls/originate', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
      10_000,
      'Call request timed out. Check your connection and try again.'
    );
  },

  recordTelemetrySessionEvents(sessionId: string, events: TelephonySessionEventInput[]): Promise<void> {
    return fetchJson(`/api/sip/telemetry/session/${sessionId}/events`, {
      method: 'POST',
      body: JSON.stringify({ events }),
    }).then(() => undefined);
  },

  recordCallMetricSamples(sessionId: string, callId: string, samples: TelephonyCallMetricSampleInput[]): Promise<void> {
    return fetchJson(`/api/sip/telemetry/calls/${callId}/samples?sessionId=${encodeURIComponent(sessionId)}`, {
      method: 'POST',
      body: JSON.stringify({ samples }),
    }).then(() => undefined);
  },
};
